import type { AllergenData } from "@/hooks/useAllergens";
import api from "@/services/api";
import type { ActiveProjectsListPayload } from "@/types/activeProjectsList.types";
import type { NotProgressedListPayload } from "@/types/notProgressedList.types";
import { toNotProgressedApiSortOrder } from "@/types/notProgressedList.types";
import { transformActiveProjectsData } from "@/utils/activeProjectsDataTransformer";
const {iamServiceAPI, productServiceAPI, glossaryServiceAPI} = api;
 
const MAIN_DETAILS_ONLOAD_PATH = `/product/specification/onload`;
const PRODUCT_INFO_DETAILS_PATH = `/productInfo/details`;
const MAIN_DETAILS_GET_PATH = `/product/specification`;
const ACTIVE_PROJECTS_LIST_PATH = `/activeProjects/list`;
const NOT_PROGRESSED_LIST_PATH = `/notProgressed/list`;
const ALLERGEN_ONLOAD_PATH = `/glossary/allergen-onload`;
export const DEFAULT_ALLERGEN_ONLOAD_VERSION = "V3";

const resolveAllergenOnloadVersion = (version?: string): string => {
  const trimmed = String(version ?? "").trim();
  return trimmed || DEFAULT_ALLERGEN_ONLOAD_VERSION;
};

const ActiveProjectsService = {
    // Build JSON store path for a tab/sub-tab with variant
    getTabPath: (activeProjectId: string | number, tab: string, variantId: number | string, subTab?: number | string): string => {
        const abbr: Record<'COMPLIANCE_DOCUMENTS' | 'MAIN_DETAILS' | 'RECIPE_AND_RAW_MATERIALS' | 'STORAGE' | 'NUTRITION' | 'ALLERGENS_AND_SENSITIVITIES' | 'PACKAGING_AND_LABELLING', string> = {
            COMPLIANCE_DOCUMENTS: 'cd',
            MAIN_DETAILS: 'md',
            RECIPE_AND_RAW_MATERIALS: 'rrm',
            STORAGE: 'st',
            NUTRITION: 'nu',
            ALLERGENS_AND_SENSITIVITIES: 'as',
            PACKAGING_AND_LABELLING: 'pl',
        };

        const idPart = String(activeProjectId);
        const variantPart = String(variantId ?? 0);
        const tabPart = abbr[tab as keyof typeof abbr] ?? String(tab).toLowerCase();

        // Format: /jsonStore/ap-{id}-{tab}-{optionalSubTab}-{variant}.json
        const parts = ['ap', idPart, tabPart, ...(subTab !== undefined ? [String(subTab)] : []), variantPart];
        const jsonKey = parts.join('-') + '.json';
        return `/jsonStore/${jsonKey}`;
    },

 
    // Get allergen onload data including allergens and declaration options
    getAllergensOnloadData: async (
        vendorAssessmentId?: string | number,
        version?: string
    ): Promise<any> => {
        try {
            const params = new URLSearchParams();
            if (vendorAssessmentId != null && String(vendorAssessmentId).trim() !== "") {
                params.set("vendorAssessmentId", String(vendorAssessmentId));
            }
            params.set("version", resolveAllergenOnloadVersion(version));
            const qs = params.toString();
            const path = qs ? `${ALLERGEN_ONLOAD_PATH}?${qs}` : ALLERGEN_ONLOAD_PATH;
            const resp = await glossaryServiceAPI.get(path);
            return {
                allergyDietaryQuestions: resp?.data?.allergyDietaryQuestions ?? [],
                otherAllergenDeclarations: resp?.data?.otherAllergenDeclarations ?? [],
                sites: resp?.data?.sites ?? []
            };
        } catch (error) {
            console.error("Error fetching allergens onload data:", error);
            throw error;
        }
    },

    // Get other allergen declarations (for dropdown options)
    getOtherAllergenDeclarations: async (
        vendorAssessmentId?: string | number,
        version?: string
    ): Promise<any> => {
        try {
            const params = new URLSearchParams();
            if (vendorAssessmentId != null && String(vendorAssessmentId).trim() !== "") {
                params.set("vendorAssessmentId", String(vendorAssessmentId));
            }
            params.set("version", resolveAllergenOnloadVersion(version));
            const qs = params.toString();
            const path = qs ? `${ALLERGEN_ONLOAD_PATH}?${qs}` : ALLERGEN_ONLOAD_PATH;
            const resp = await glossaryServiceAPI.get(path);
            return resp?.data?.otherAllergenDeclarations ?? [];
        } catch (error) {
            console.error("Error fetching other allergen declarations:", error);
            throw error;
        }
    },

    // Get only allergen questions data (transformed for backward compatibility)
    getAllergensData: async (
        vendorAssessmentId?: string | number,
        version?: string
    ): Promise<any> =>{
        try {
            const params = new URLSearchParams();
            if (vendorAssessmentId != null && String(vendorAssessmentId).trim() !== "") {
                params.set("vendorAssessmentId", String(vendorAssessmentId));
            }
            params.set("version", resolveAllergenOnloadVersion(version));
            const qs = params.toString();
            const path = qs ? `${ALLERGEN_ONLOAD_PATH}?${qs}` : ALLERGEN_ONLOAD_PATH;
            const resp = await glossaryServiceAPI.get(path);
            const allergyDietaryQuestions = resp?.data?.allergyDietaryQuestions ?? [];
            
            // Transform API response to match expected format
            return allergyDietaryQuestions.map((item: any) => ({
                id: item?.id,
                allergen: item?.name,
                allergenDescription: item?.description,
                label: item?.label,
                isSpecify: item?.isSpecify // Include isSpecify field from API
            }));
        } catch (error) {
            console.error("Error fetching allergens data:", error);
            throw error;
        }
    },

    updateAllergenData: async (allergenData: any): Promise<any> => {
        try {
            // In a real implementation, this would call the API
            // const resp = await iamServiceAPI.put(`/allergens/${allergenData?.id}`, allergenData);
            
            return {
                success: true,
                data: allergenData,
                message: "Allergen updated successfully"
            };
        } catch (error) {
            console.error("Error updating allergen data:", error);
            throw error;
        }
    },

    saveAllergensData: async (allergensData: any): Promise<any> => {
        try {
            // In a real implementation, this would call the API to save all allergens
            // const resp = await iamServiceAPI.post('/allergens/bulk-save', { allergens: allergensData });
            
            return {
                success: true,
                data: allergensData,
                message: "All allergens saved successfully"
            };
        } catch (error) {
            console.error("Error saving allergens data:", error);
            throw error;
        }
    },


    // Fetch tab data by generated JSON key
    // (Unified signature - variantId and subTab are optional)
    getTabData: async (activeProjectId: string | number, tab: string, variantId?: number | string, subTab?: number | string): Promise<any> => {
        try {
            const path = ActiveProjectsService.getTabPath(activeProjectId, tab, variantId as any, subTab as any);
            const resp = await iamServiceAPI.get(path);
            return resp.data;
        } catch (error) {
            console.error('❌ Error fetching tab data:', error);
            throw error;
        }
    },

    // Save tab data by generated JSON key (Unified signature)
    saveTabData: async (data: any, activeProjectId: string | number, tab: string, variantId?: number | string, subTab?: number | string): Promise<any> => {
        try {
            const path = ActiveProjectsService.getTabPath(activeProjectId, tab, variantId as any, subTab as any);
            const resp = await iamServiceAPI.post(path, data);
            return resp.data;
        } catch (error) {
            console.error('❌ Error saving tab data:', error);
            throw error;
        }
    },
    /**
     * Active Projects — paged list (POST).
     * Response: bare array (legacy) or `{ ideas: unknown[]; isFilteredIdea?: boolean }`.
     * Returns raw `resp.data` so list controllers can unwrap the envelope.
     */
    postActiveProjectsList: async (payload: ActiveProjectsListPayload): Promise<unknown> => {
        try {
            const resp = await productServiceAPI.post(ACTIVE_PROJECTS_LIST_PATH, payload);
            return resp?.data;
        } catch (error) {
            console.error("❌ Error fetching active projects list (POST):", error);
            throw error;
        }
    },

    /**
     * Not Progressed tab — paged list (POST).
     * Response: bare array (legacy) or `{ ideas: unknown[]; isFilteredIdea?: boolean }`.
     */
    postNotProgressedList: async (payload: NotProgressedListPayload): Promise<unknown> => {
        try {
            const sortBy = String(payload.sortBy ?? "").trim();
            const apiPayload: NotProgressedListPayload = {
                ...payload,
                sortBy,
                sortOrder: sortBy ? toNotProgressedApiSortOrder(payload.sortOrder) : "",
            };
            const resp = await productServiceAPI.post(NOT_PROGRESSED_LIST_PATH, apiPayload);
            return resp?.data;
        } catch (error) {
            console.error("❌ Error fetching not progressed list (POST):", error);
            throw error;
        }
    },

    getActiveProjectsList: async (isVendor: boolean = false): Promise<any> =>{
        try {
            const resp = await productServiceAPI.get("/activeProjects");
            
            // Comprehensive null checks for API response
            if (!resp) {
                console.warn('⚠️ [activeProjectsService] No response from API');
                return { data: [] };
            }
            
            if (!resp.data) {
                console.warn('⚠️ [activeProjectsService] No data in API response');
                return { data: [] };
            }
            
            const apiData = Array.isArray(resp.data) ? resp.data : [];
            
            // Additional safety check for empty or invalid data
            if (!Array.isArray(apiData)) {
                console.warn('⚠️ [activeProjectsService] API data is not an array:', typeof apiData);
                return { data: [] };
            }
            
            // Transform the API data to ensure consistent structure
            // Pass isVendor flag to transformer to determine which status to display
            const transformedApiData = transformActiveProjectsData(apiData, isVendor);

            // const transformedApiData = transformActiveProjectsData(apiData);            
            // Ensure transformed data is valid
            if (!Array.isArray(transformedApiData)) {
                console.warn('⚠️ [activeProjectsService] Transformed data is not an array');
                return { data: [] };
            }
            
            // Get mock data
            
            // Combine: API data first, then mock data
            // const combinedData = [...transformedApiData, ...mockData];
                        
            return { 
                data: transformedApiData,
                apiDataCount: transformedApiData.length
            };
        } catch (error) {
            console.error("❌ Error fetching active projects from API:", error);
            
            // If API fails completely, return empty array
            return { 
                data: [],
                apiDataCount: 0,
                error: error instanceof Error ? error.message : 'Failed to fetch active projects'
            };
        }
    },

    // Obsolete function - not used
    getTabsNVariantsList: async (productId?: string|number): Promise<any> => {
        try {
            // Fetch the product from API instead of mock data
            const resp = await productServiceAPI.get(`/activeProjects/${productId}/tabs-variants`);
            
            // If API doesn't have this endpoint yet, construct response from active projects list
            if (!resp?.data) {
                // Fallback: get product from active projects list API
                const projectsResponse = await ActiveProjectsService.getActiveProjectsList();
                const projects = projectsResponse?.data ?? [];
                const product = projects.find((p: any) => p.id === String(productId));
                
                let variants: Array<{id: number, name: string, disabled: boolean}> = [];
                
                if (product?.variants?.length > 0) {
                    // Map the new variant structure to the expected format
                    variants = product.variants.map((variant: any) => ({
                        id: variant.variantId,
                        name: variant.variantName,
                        disabled: false
                    }));
                }

                return {
                    tabs: [
                        { id: 1, name: 'COMPLIANCE_DOCUMENTS', disabled: false },
                        { id: 2, name: 'MAIN_DETAILS', disabled: false },
                        { id: 3, name: 'RECIPE_AND_RAW_MATERIALS', disabled: false },
                        { id: 4, name: 'STORAGE', disabled: false },
                        { id: 5, name: 'NUTRITION', disabled: false },
                        { id: 6, name: 'ALLERGENS_AND_SENSITIVITIES', disabled: false },
                        { id: 7, name: 'PACKAGING_AND_LABELLING', disabled: false }
                    ],
                    variants: variants,
                    // Include additional product metadata for debugging
                    productMetadata: product ? {
                        title: product.title,
                        giftPack: product.giftPack,
                        amountOfProductsInGiftPack: product.amountOfProductsInGiftPack
                    } : null
                };
            }
            
            return resp.data;
        } catch (error) {
            console.error("❌ Error fetching tabs/variants list:", error);
            throw new Error(
                error instanceof Error 
                    ? `Failed to fetch tabs/variants: ${error.message}`
                    : 'Failed to fetch tabs/variants from API'
            );
        }
    },

    // 🎯 Get dropdown options from mock API
    getDropdownOptions: async (category?: string): Promise<any> => {
        try {
            
            // Build API URL with optional category filter
            const baseUrl = '/api/active-projects/dropdowns';
            const url = category ? `${baseUrl}?category=${category}` : baseUrl;
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error. status: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (!result.success) {
                throw new Error(result.message || 'Failed to fetch dropdown options');
            }
            
            return {
                success: true,
                data: result.data,
                message: result.message,
                meta: result.meta || {}
            };
            
        } catch (error) {
            console.error('❌ Error fetching dropdown options from API:', error);
            
            // Fallback to simple mock data if API fails
            const fallbackData = {
                categories: [
                    { id: 1, label: "Food & Beverages", value: "food_beverages" },
                    { id: 2, label: "Health & Beauty", value: "health_beauty" },
                    { id: 3, label: "Pet Products", value: "pet_products" }
                ],
                statuses: [
                    { id: 1, label: "Active", value: "active" },
                    { id: 2, label: "On Hold", value: "on_hold" },
                    { id: 3, label: "Archived", value: "archived" }
                ]
            };
            
            return {
                success: true,
                data: fallbackData,
                message: 'Using fallback dropdown options due to API error',
                fallback: true
            };
        }
    },

    // 🔄 Get dynamic options from actual active projects data
    getDynamicDropdownOptions: async (): Promise<any> => {
        try {
            // Get active projects data from API
            const projectsResponse = await ActiveProjectsService.getActiveProjectsList();
            const projects = projectsResponse?.data || [];
            
            const extractUniqueValues = (field: string) => {
                const values = projects.map((project: any) => project[field]).filter(Boolean);
                const uniqueValues = Array.from(new Set(values));
                return uniqueValues.map((value, index) => ({
                    id: index + 1,
                    label: value,
                    value: String(value).toLowerCase().replace(/\s+/g, '_')
                }));
            };

            return {
                success: true,
                data: {
                    categories: extractUniqueValues('productSpecificationType'), // Updated field name
                    statuses: extractUniqueValues('status'),
                    categoryManagers: extractUniqueValues('categoryManagerUser'), // Updated field name
                    productCreators: extractUniqueValues('productCreator') // Updated field name
                },
                message: 'Successfully extracted dynamic dropdown options from active projects data'
            };
        } catch (error) {
            console.error('❌ Error extracting dynamic dropdown options:', error);
            throw error;
        }
    },

 

    // getMainDetails: async (): Promise<any> => {
    //     try {
    //         const resp = await iamServiceAPI.get(PATHS.MAIN_DETAILS);
    //         return resp.data;
    //     } catch (error) {
    //         throw error;
    //     }
    // },
    // saveMainDetails: async (data: any): Promise<any> => {
    //     try {
    //         const resp = await iamServiceAPI.post(PATHS.MAIN_DETAILS, data);
    //         return resp.data;
    //     } catch (error) {
    //         throw error;
    //     }
    // }

    saveWorkflowData: async (data: any): Promise<any> => {
        try {
            const path =  `/jsonStore/workflow-status.json`;
            const resp = await iamServiceAPI.post(path, data);
            return resp.data;
        } catch (error) {
            console.error('❌ Error saving tab data:', error);
            throw error;
        }
    },
    getWorkflowData: async (): Promise<any> => {
        try {
            const path =  `/jsonStore/workflow-status.json`;
            const resp = await iamServiceAPI.get(path);
            return resp.data;
        } catch (error) {
            console.error('❌ Error fetching tab data:', error);
            throw error;
        }
    },
    getMainDetailsOnloadData: async (type?: string): Promise<any> => {
        try {
            let url = MAIN_DETAILS_ONLOAD_PATH;
            
            // Add type parameter if provided
            if (type) {
                url += `?type=${type}`;
            }
            
            const resp = await productServiceAPI.get(url);
            return resp.data;
        } catch (error) {
            console.error('❌ Error fetching tab data:', error);
            throw error;
        }
    },

    // Get product info details by ideaId
    getProductInfoDetails: async (ideaId: string | number): Promise<any> => {
        try {
            const url = `${PRODUCT_INFO_DETAILS_PATH}/${ideaId}`;
            const resp = await productServiceAPI.get(url);
            return resp?.data;
        } catch (error) {
            console.error('❌ Error fetching product info details:', error);
            throw error;
        }
    },

    // Get MainDetails data from real API endpoint
    getMainDetailsData: async (productSpecificationId: string | number, vendorAssessmentId: string | number): Promise<any> => {
        if (!vendorAssessmentId) {
            throw new Error('Invalid parameters for getMainDetailsData');
        }
        try {
            const base = `${MAIN_DETAILS_GET_PATH}/${vendorAssessmentId}`;
            const resp = await productServiceAPI.get(base);
            return resp?.data;
        } catch (error) {
            console.error('❌ Error fetching MainDetails from real API:', error);
            throw error;
        }
    },

    // Save MainDetails data to real API endpoint using PUT
    saveMainDetailsData: async (data: any, productSpecificationId: string | number, vendorAssessmentId: string | number): Promise<any> => {
        try {
            if (!data || !vendorAssessmentId) {
                throw new Error('Invalid parameters for saveMainDetailsData');
            }

            const base = `${MAIN_DETAILS_GET_PATH}/${vendorAssessmentId}`;
            const resp = await productServiceAPI.put(base, data);
            return resp;
        } catch (error) {
            console.error('❌ Error saving MainDetails to real API:', error);
            throw error;
        }
    },

    // 📥 Packaging GET API - explicit getter for packaging details
    getPackagingDetails: async (productId: string | number, vendorAssessmentId?: string | number): Promise<any> => {
        try {
            const base = `/product/specification/packaging-details/${productId}`;
            const url = vendorAssessmentId ? `${base}?vendorAssessmentId=${encodeURIComponent(String(vendorAssessmentId))}` : base;
            const resp = await productServiceAPI.get(url);
            return resp.data;
        } catch (error) {
            console.error('❌ Error fetching packaging details:', error);
            throw error;
        }
    },

    // 💾 Other Labeling tab save API (PUT)
    savePackagingData: async (otherLabelingTabId: string | number, payload: any): Promise<any> => {
        try {
            const resp = await productServiceAPI.put(
              `/product/other-labeling-tab/${encodeURIComponent(String(otherLabelingTabId))}`,
              payload
            );
            return resp.data;
        } catch (error) {
            console.error('❌ Error saving packaging data:', error);
            throw error;
        }
    },
};

export default ActiveProjectsService;

